import { VideoDetailsDialogComponent } from '@go-modules/components/dialogs/video-details-dialog/video-details-dialog.component';
import { VideoShareDialogService } from '@go-modules/components/dialogs/video-share-dialog/video-share-dialog.service';
import { EnvironmentVarsService } from '@go-modules/services/environment-vars/environment-vars.service';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import { NgxSessionService } from '@go-modules/services/session/session.service';
import type { ScoreOverrideResponse } from '@go-modules/services/session/session.service';
import { DowngradeModalService } from '@go-modules/services/downgrade-modal/downgrade-modal.service';
import { NgxFeatureFlagService } from '@go-modules/services/feature-flag/feature-flag.service';
import { NgxGoToastService } from '@go-modules/services/go-toast/go-toast.service';
import { GoToastStatusType } from '@go-modules/enums/go-toast-status-type';
import shareIcon from '@fortawesome/fontawesome-pro/svgs/regular/share-nodes.svg';

export enum TooltipAndAriaLabel {
	tooltip = 'tooltip',
	ariaLabel = 'ariaLabel'
}

export class FeedbackSessionHeaderController {

	public readonly;
	public doneButtonText: string;
	public options: any;
	public doneAnalyticsActions: any;
	public mainController: any;
	public environmentVarsService: EnvironmentVarsService;
	public tooltipAndAriaLabel = TooltipAndAriaLabel;
	public scoreAriaLabel: string;
	public isPosted: boolean;
	public shareIcon = shareIcon;

	/* @ngInject */
	constructor (
		private sessionEditor,
		private $analytics,
		private MediaModel,
		private feedbackSession,
		private $translate: ng.translate.ITranslateService,
		private ngxGoModalService: GoModalService,
		private ngxVideoShareDialogService: VideoShareDialogService,
		private ngxSessionService: NgxSessionService,
		private $injector: ng.auto.IInjectorService,
		private $q: ng.IQService,
		private ngxDowngradeModalService: DowngradeModalService,
		private featureFlag: NgxFeatureFlagService,
		private ngxGoToastService: NgxGoToastService
	) {
		this.environmentVarsService = EnvironmentVarsService.getInstance();
	}

	/**
	 * Handle controller init
	 */
	public $onInit () {
		this.readonly = this.environmentVarsService.get(EnvironmentVarsService.VAR.READONLY);
		this.doneButtonText = this.getDoneButtonText();
		this.doneAnalyticsActions = {
			common_leave: 'leave',
			common_finish: 'finish',
			common_back: 'back',
			common_cancel: 'cancel'
		};

		this.isPosted = this.mainController.session.isPosted();
	}

	public shouldShowShareButton (): boolean {
		const ngxSessionService: NgxSessionService = this.$injector.get('ngxSessionService');
		return ngxSessionService.hasVideoSharePermission(this.mainController.session);
	}

	public shareSession () {
		this.ngxVideoShareDialogService.open({
			orgSettings: this.mainController.orgSettings,
			sessionId: this.mainController.session.session_id
		}).subscribe(() => {});
	}

	/**
	 * Whether presenter names should be shown
	 *
	 * @returns {boolean}
	 */
	public showPresenterNames () {
		return this.mainController.session.presenters.length > 0;
	}

	/**
	 * Whether video details should be shown
	 *
	 * @returns {boolean}
	 */
	public showVideoDetails () {
		return this.mainController.user.is_root_user &&
		!this.feedbackSession.isRecordMode();
	}

	/**
	 * Open the media details modal
	 *
	 * @param event
	 * @returns {*}
	 */
	public openMediaDetailsModal (event: Event) {
		event.preventDefault();
		event.stopPropagation();

		return this.ngxGoModalService.open(VideoDetailsDialogComponent, true, {
			data: {
				session: this.mainController.session
			}
		});
	}

	/**
	 * Add slide deck to session
	 */
	public addSlideDeck () {
		this.sessionEditor.addStimulus(
			this.mainController.session,
			this.mainController.activity,
			this.mainController.userGroup
		).result.then((sourceMedia) => {
			this.mainController.session.setSourceMedia(sourceMedia);
			this.mainController.session.save();
			this.$analytics.eventTrack('add-deck', {
				category: 'dashboard'
			});
			this.mainController.validateSourceMedia();
		});
	}

	/**
	 * Handles exit
	 */
	public exit () {
		this.mainController.requestExit();
	}

	public canSeeScore () {
		const group = this.mainController.userGroup;
		const activity = this.mainController.activity;

		return group.hasReviewerRole(true) ||
			this.mainController.session.isOwner(this.mainController.user) &&
			!activity.shouldHoldFeedback(group.hasPresenterRole());
	}

	public shouldDisplayScore () {
		return !this.feedbackSession.isRecordMode();
	}

	public mayEditScore () {
		if (this.environmentVarsService.get(EnvironmentVarsService.VAR.READONLY)) {
			return false;
		}

		return this.canOverrideScore();
	}

	public canOverrideScore (): boolean {
		if (this.mainController.activity.rubric_template_id === null) {
			return true;
		}

		return this.featureFlag.isAvailable('COMBINED_RUBRIC_SCORING') &&
			this.mainController.session.can_override_score === true;
	}

	public onScoreChange (score: number, previousScore: number | null) {
		if (this.mainController.activity.rubric_template_id === null) {
			return this.mainController.grade(score, previousScore);
		}

		if (!this.canOverrideScore()) {
			this.mainController.session.score = previousScore;
			return this.$q.resolve();
		}

		return this.$q.when(this.ngxDowngradeModalService.openConfirmDialog({
			title: this.$translate.instant('feedback-session-header_override-score-title'),
			message: this.$translate.instant('feedback-session-header_override-score-message', {
				score,
				possible: this.mainController.activity.getTotalPoints()
			}),
			confirmText: this.$translate.instant('feedback-session-header_override-score-confirm')
		})).then(
			() => this.saveScoreOverride(score, previousScore),
			() => {
				this.mainController.session.score = previousScore;
				return null;
			}
		);
	}

	private saveScoreOverride (score: number, previousScore: number | null) {
		const session = this.mainController.session;
		session.appended = session.appended || {};
		session.appended.previousScore = previousScore;

		const deferred = this.$q.defer<ScoreOverrideResponse>();
		this.ngxSessionService.postScoreOverride(session.getId(), score).subscribe({
			next: (response) => {
				session.setScore(response.session.score);
				session.graded_at = response.session.graded_at;
				session.is_graded = response.session.is_graded;
				deferred.resolve(response);
			},
			error: (error) => deferred.reject(error)
		});

		return deferred.promise.catch(() => {
			if (session.appended?.previousScore === previousScore) {
				delete session.appended.previousScore;
				if (!Object.keys(session.appended).length) {
					delete session.appended;
				}
			}

			this.ngxGoToastService.createToast({
				type: GoToastStatusType.ERROR,
				message: 'feedback-session-header_score-save-error',
				hideToastTime: 10000
			});
			return null;
		});
	}

	/**
	 * Get label for score display
	 *
	 * @param score
	 * @returns {string}
	 */
	public getScoreLabel (score) {
		let label = '—';
		score = parseFloat(score);

		if (!isNaN(score)) {
			score = Math.round(score * 100) / 100 + ''; // round to 2 decimal places
			label = score;
			this.scoreAriaLabel = this.$translate.instant('feedback-session-feedback-display_points-assigned', { score });
		} else if (this.mayEditScore() && !this.mainController.session.isScoreAssigned()) {
			label = this.$translate.instant('feedback-session_controller-placeholder-pts');
			this.scoreAriaLabel = this.$translate.instant('feedback-session-feedback-display_no-points-assigned');
		}
		return label;
	}

	public getScoreTooltipAndAriaLabel (type: string) {
		let ariaLabel = '';
		let tooltip = '';
		const score = this.mainController.session.score;

		if (this.mainController.activity.isRubricEnabled()) {
			tooltip = this.$translate.instant('feedback-session-feedback-display_score-calculated');

			if (score === null) {
				ariaLabel = this.$translate.instant('feedback-session-feedback-display_no-rubric-no-score-aria-label');
			} else {
				ariaLabel = this.$translate.instant('feedback-session-feedback-display_no-rubric-has-score-aria-label', {
					score: Math.round(score * 100) / 100 + '',
					possible: this.mainController.activity.getTotalPoints()
				});
			}
		} else {
			if (score === null) {
				tooltip = this.$translate.instant('feedback-session-feedback-display_no-points-assigned');
				ariaLabel = this.$translate.instant('feedback-session-feedback-display_no-points-assigned');
			} else {
				const scorePossible = {
					score: Math.round(score * 100) / 100 + '',
					possible: this.mainController.activity.getTotalPoints()
				};

				tooltip = this.$translate.instant('feedback-session-feedback-display_points-possible', scorePossible);
				ariaLabel = this.$translate.instant('feedback-session-feedback-display_points-graded', scorePossible);
			}
		}

		return type === TooltipAndAriaLabel.tooltip ? tooltip : ariaLabel;
	}

	public shouldShowSpeedGrader () {
		const sessionList = this.mainController.options.speedGraderList;
		return sessionList && sessionList.length &&
			this.mainController.userGroup.hasReviewerRole(true) &&
			!this.feedbackSession.isRecordMode() &&
			this.feedbackSession.lastMediaDisplayMode !== this.feedbackSession.MediaDisplayMode.RECORD;
	}

	/**
	 * Done buton text
	 */
	private getDoneButtonText () {
		const displayMode = this.feedbackSession.options.mediaDisplayMode;
		const MediaDisplayMode = this.feedbackSession.MediaDisplayMode;
		const activity = this.mainController.activity;
		const session = this.mainController.session;
		const user = this.mainController.user;

		if (displayMode === MediaDisplayMode.CONVERSATION ||
			displayMode ===  MediaDisplayMode.LIVE ||
			(displayMode === MediaDisplayMode.PLAYBACK &&
				this.feedbackSession.lastMediaDisplayMode === MediaDisplayMode.RECORD &&
				activity.is_conversation === true &&
				activity.live_session_enabled !== true &&
				!activity.has_single_recording_attempt) ||
			(this.mainController.activity.isCommentOnlySingleAttempt()
				&& session.isLimitCommentOnlySingleAttempt(activity, user)) ||
			(displayMode === MediaDisplayMode.RECORD && this.mainController.activity.isSingleAttempt()))
		{
			return 'common_leave';
		} else if (displayMode === MediaDisplayMode.PLAYBACK &&
			this.mainController.activity.isCommentOnlySingleAttempt() &&
			//the following function checks if the FeedbackCreator should be hidden, which will just
			//be on subsequent session views, we only want to show 'finish' if it is the first time
			!session.isLimitCommentOnlySingleAttempt(activity, user)){
			return 'common_finish';
		} else if (displayMode === MediaDisplayMode.PLAYBACK) {
			return 'common_back';
		} else {
			return 'common_cancel';
		}
	}
}
